import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, TrendingUp } from "lucide-react";

/**
 * RevenueSpotlight — a persuasive, non-pushy panel that surfaces
 * revenue-earning services alongside outcome-driven copy.
 *
 * Rendered on founder / investor dashboards to keep monetisation front-of-mind
 * without shouting. Two forms:
 *   • hero: dark navy card (used when the user hasn't purchased anything yet)
 *   • pair: two side-by-side option cards (used once user is engaged)
 */
export function RevenueSpotlight({
  eyebrow = "GROW WITH VENTURAZ",
  title,
  subtitle,
  bullets = [],
  primary,       // { href, label, price? }
  secondary,     // { href, label }
  variant = "hero",
}) {
  if (variant === "hero") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-[20px] liquid-morph-hero text-white p-6 lg:p-8 mb-6"
        data-testid="revenue-spotlight"
      >
        <div className="absolute inset-0 pointer-events-none opacity-60" aria-hidden>
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gold/15 blur-3xl" />
          <div className="absolute -bottom-32 -left-10 w-80 h-80 rounded-full bg-forest-mid/25 blur-3xl" />
        </div>
        <div className="relative flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-10">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {eyebrow}
            </div>
            <h3 className="mt-2 font-display text-2xl lg:text-3xl leading-tight text-white max-w-xl">
              {title}
            </h3>
            {subtitle && (
              <p className="mt-2 text-sm text-white/85 max-w-xl leading-relaxed">
                {subtitle}
              </p>
            )}
            {bullets.length > 0 && (
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 max-w-xl">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white">
                    <Check size={14} strokeWidth={3} className="text-gold flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-end gap-2 lg:gap-2 lg:min-w-[220px]">
            {primary && (
              <Link
                to={primary.href}
                className="inline-flex items-center justify-center gap-1.5 px-5 py-3 bg-gold hover:bg-gold-hover text-navy-deep font-heading font-semibold text-sm rounded-full transition-all hover:-translate-y-0.5"
                data-testid="revenue-spotlight-primary"
              >
                {primary.label}
                <ArrowRight size={14} />
              </Link>
            )}
            {primary?.price && (
              <div className="text-center lg:text-right text-[11px] font-mono text-white/80">
                {primary.price}
              </div>
            )}
            {secondary && (
              <Link
                to={secondary.href}
                className="text-center text-xs text-white/85 hover:text-white underline underline-offset-4"
                data-testid="revenue-spotlight-secondary"
              >
                {secondary.label}
              </Link>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // 'pair' or 'compact' variant — not used yet
  return null;
}

/**
 * ServiceCardTrio — three understated, editorial service cards. Each hints at
 * an outcome ("Get a Deep DD report", "Have a pitch reviewed") and links to
 * the actual purchase surface. No hard sell, no discount urgency — just
 * clarity about what's on offer.
 */
export function ServiceCardTrio({ eyebrow = "SERVICES", title, items = [] }) {
  if (!items.length) return null;
  return (
    <div className="mb-6" data-testid="service-card-trio">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-ink/50">{eyebrow}</div>
          {title && <div className="font-heading text-lg text-navy-deep mt-1">{title}</div>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.slice(0, 3).map((it, i) => (
          <motion.div
            key={it.id || i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, delay: 0.04 * i, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -3 }}
            className="group relative bg-white rounded-[16px] border border-parchment hover:border-gold/50 p-5 transition-all shadow-[0_2px_10px_rgba(15,61,46,0.03)] hover:shadow-[0_10px_28px_rgba(15,61,46,0.08)]"
            data-testid={`service-card-${it.id || i}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-[10px] bg-gold/10 text-gold flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                {it.icon ? <it.icon size={18} strokeWidth={2.2} /> : <TrendingUp size={18} strokeWidth={2.2} />}
              </div>
              {it.priceLabel && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink/45 pt-1">
                  {it.priceLabel}
                </div>
              )}
            </div>
            <div className="font-heading text-base text-navy-deep">{it.title}</div>
            <div className="mt-1.5 text-xs text-ink/60 leading-relaxed">{it.description}</div>
            <Link
              to={it.href}
              className="mt-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-navy-deep hover:text-gold transition-colors"
              data-testid={`service-card-cta-${it.id || i}`}
            >
              {it.cta || "Learn more"} <ArrowRight size={12} />
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
